// The hidden play-host iframe that warms video bytes on the publisher page.
import type { HostPixmoto } from "@/types/globals";
import type { WarmConfig } from "@/modules/experiencePrefetchWarm";

/**
 * Hard cap before the hidden warm iframe is torn down. Kept short: a few
 * seconds of buffered video is enough for an instant start, and warm now
 * fires on user intent (hover/tap), so this bounds each intentional warm.
 */
const VIDEO_WARM_MAX_MS = 3500;

/**
 * Warm via a hidden iframe pointing at the play server.
 *
 * Why: media.pixmoto.com has a Referer allowlist that only accepts pixmoto.com.
 * A <video preload> on the publisher origin sends the publisher's Referer and
 * is rejected. The play-host iframe runs the same player code that the click-
 * time popover opens, but inside it requests reach media.pixmoto.com with
 * Referer = play.pixmoto.com → allowed. Bytes land in HTTP cache (partitioned
 * by top-level publisher origin), so the click-time iframe gets them warm.
 */
export function warmViaPlayIframe(
  playServer: string,
  expId: unknown,
  mapUrl: unknown,
  cfg: WarmConfig,
): Promise<boolean> {
  if ((!expId && !mapUrl) || !playServer || typeof document === "undefined") {
    return Promise.resolve(false);
  }
  return new Promise(function (resolve) {
    const protocol =
      (cfg && cfg.protocol) ||
      (typeof location !== "undefined" ? location.protocol : "https:");
    const path = expId ? `/${encodeURIComponent(String(expId))}` : "/";
    let qs = "pix-embed-shell=1&pix-warm-only=1";
    if (cfg && cfg.device) {
      qs += `&pix-device=${encodeURIComponent(cfg.device)}`;
    }
    if (!expId && mapUrl) {
      qs += `&url=${encodeURIComponent(mapUrl as string)}`;
    }
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.setAttribute("tabindex", "-1");
    iframe.setAttribute("title", "Pixmoto media warmer");
    iframe.style.cssText =
      "position:absolute;width:1px;height:1px;left:-9999px;top:-9999px;opacity:0;pointer-events:none;border:0";
    iframe.src = `${protocol}//${playServer}${path}?${qs}`;

    const pm = window.__pixmoto || {};
    window.__pixmoto = pm;
    // Supersede any prior warm: cancels its promise + removes its iframe.
    _releaseWarmIframe(pm);

    let done = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const finish = function (ok: boolean) {
      if (done) {
        return;
      }
      done = true;
      if (timer) {
        clearTimeout(timer);
      }
      try {
        if (iframe.parentNode) {
          iframe.parentNode.removeChild(iframe);
        }
      } catch (_e) {
        /* ignore */
      }
      const pm2 = window.__pixmoto;
      if (pm2 && pm2._publisherWarmIframeEl === iframe) {
        pm2._publisherWarmIframeEl = null;
        pm2._publisherWarmFinish = null;
      }
      resolve(!!ok);
    };

    iframe.addEventListener(
      "error",
      function () {
        finish(false);
      },
      { once: true },
    );

    pm._publisherWarmIframeEl = iframe;
    pm._publisherWarmFinish = finish;

    /*
     * Defer the actual iframe load to post-`window.load` so the warm
     * subresource never delays the publisher's `load` event (analytics,
     * Adsense, LCP-adjacent measurements). After load, attach + start
     * the 12 s ceiling. If a supersession comes in before load fires,
     * `_releaseWarmIframe` resolves this promise as failed and `done`
     * gates the attach below.
     */
    const attach = function () {
      if (done) {
        return;
      }
      (document.body || document.documentElement).appendChild(iframe);
      timer = setTimeout(function () {
        finish(true);
      }, VIDEO_WARM_MAX_MS);
    };
    if (typeof document !== "undefined" && document.readyState === "complete") {
      attach();
    } else if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("load", attach, { once: true });
    } else {
      attach();
    }
  });
}

function _releaseWarmIframe(pm: HostPixmoto | null | undefined) {
  if (!pm) {
    return;
  }
  if (pm._publisherWarmFinish) {
    // Resolves the in-flight promise as failed, clears timer, removes iframe.
    const f = pm._publisherWarmFinish;
    pm._publisherWarmFinish = null;
    f(false);
    return;
  }
  if (pm._publisherWarmIframeEl) {
    const iframe = pm._publisherWarmIframeEl;
    try {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    } catch (_e) {
      /* ignore */
    }
    pm._publisherWarmIframeEl = null;
  }
}

export function releasePublisherWarmVideo() {
  const pm = typeof window !== "undefined" ? window.__pixmoto : null;
  _releaseWarmIframe(pm);
}
